import { useCallback, useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  Tab,
  Tabs,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import LogoutIcon from "@mui/icons-material/Logout";
import HomeIcon from "@mui/icons-material/Home";
import axios from "axios";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { clearAuth, getAuthHeaders } from "./auth";
import SuperAdminCrmPanel from "./SuperAdminCrmPanel";

function TabPanel({ hidden, children }) {
  if (hidden) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

const emptyEdit = {
  id: "",
  email: "",
  firstname: "",
  lastname: "",
  phone: "",
  address: "",
  role: "customer",
  planKey: "",
  subscriptionStatus: "",
  billingID: "",
  subscriptionId: "",
  stripeID: "",
  quoteNotificationEmail: "",
  platformNote: "",
  featureEmbedScript: true,
  featureMultiParcelQuotes: true,
  featureStripeConnect: true,
  featureAiAssistBeta: false,
};

export default function SuperAdminApp() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState(null);
  const [usersLoading, setUsersLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState(emptyEdit);
  const [saving, setSaving] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await axios.get("/superadmin/summary", { headers: getAuthHeaders() });
      setSummary(data);
    } catch (e) {
      setSummary(null);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setError("");
    try {
      const { data } = await axios.get("/superadmin/users", {
        headers: getAuthHeaders(),
        params: {
          page: page + 1,
          limit: pageSize,
          q: searchQuery.trim() || undefined,
          role: roleFilter || undefined,
        },
      });
      setRows(
        (data.items || []).map((u) => ({
          id: u._id,
          ...u,
        }))
      );
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.response?.data?.message || "Could not load users.");
      setRows([]);
    } finally {
      setUsersLoading(false);
    }
  }, [page, pageSize, searchQuery, roleFilter]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const applyFilters = () => {
    setPage(0);
    setSearchQuery(searchInput);
  };

  const openEdit = (row) => {
    setEdit({
      id: row.id,
      email: row.email || "",
      firstname: row.firstname || "",
      lastname: row.lastname || "",
      phone: row.phone || "",
      address: row.address || "",
      role: row.role || "customer",
      planKey: row.planKey || "",
      subscriptionStatus: row.subscriptionStatus || "",
      billingID: row.billingID || "",
      subscriptionId: row.subscriptionId || "",
      stripeID: row.stripeID || "",
      quoteNotificationEmail: row.quoteNotificationEmail || "",
      platformNote: row.platformNote || "",
      featureEmbedScript: row.featureEmbedScript !== false,
      featureMultiParcelQuotes: row.featureMultiParcelQuotes !== false,
      featureStripeConnect: row.featureStripeConnect !== false,
      featureAiAssistBeta: row.featureAiAssistBeta === true,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.patch(
        `/superadmin/users/${edit.id}`,
        {
          firstname: edit.firstname,
          lastname: edit.lastname,
          phone: edit.phone,
          address: edit.address,
          role: edit.role,
          planKey: edit.planKey,
          subscriptionStatus: edit.subscriptionStatus,
          quoteNotificationEmail: edit.quoteNotificationEmail,
          platformNote: edit.platformNote,
          featureEmbedScript: edit.featureEmbedScript,
          featureMultiParcelQuotes: edit.featureMultiParcelQuotes,
          featureStripeConnect: edit.featureStripeConnect,
          featureAiAssistBeta: edit.featureAiAssistBeta,
        },
        { headers: getAuthHeaders() }
      );
      setEditOpen(false);
      await loadUsers();
      await loadSummary();
    } catch (e) {
      setError(e.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    clearAuth();
    navigate("/login", { replace: true });
  };

  const columns = [
    { field: "email", headerName: "Email", flex: 1, minWidth: 180 },
    {
      field: "name",
      headerName: "Name",
      width: 160,
      valueGetter: (params) =>
        `${params.row.firstname || ""} ${params.row.lastname || ""}`.trim() || "—",
    },
    { field: "role", headerName: "Role", width: 110 },
    { field: "planKey", headerName: "Plan", width: 100 },
    { field: "subscriptionStatus", headerName: "Subscription", width: 120 },
    {
      field: "stripe",
      headerName: "Connect",
      width: 90,
      valueGetter: (p) => (p.row.stripeID && String(p.row.stripeID).startsWith("acct_") ? "Yes" : "—"),
    },
    {
      field: "actions",
      headerName: "",
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Edit account">
          <IconButton size="small" onClick={() => openEdit(params.row)} aria-label="Edit user">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ flexGrow: 1, minHeight: "100vh", bgcolor: "grey.50" }}>
      <AppBar position="sticky" color="inherit" elevation={0} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 800, letterSpacing: "-0.02em" }}>
            Clibbins platform
          </Typography>
          <Button component={RouterLink} to="/" size="small" startIcon={<HomeIcon />} sx={{ mr: 1 }}>
            Marketing site
          </Button>
          <Button size="small" color="inherit" startIcon={<LogoutIcon />} onClick={logout}>
            Log out
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 1200, mx: "auto", px: 2, py: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider", mb: 0 }}>
          <Tab label="Overview" />
          <Tab label="Users & billing" />
        </Tabs>

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        <TabPanel hidden={tab !== 0}>
          {!summary ? (
            <CircularProgress sx={{ display: "block", mx: "auto", my: 4 }} />
          ) : (
            <Grid container spacing={2}>
              {[
                ["Total users", summary.totalUsers],
                ["Operators (admin)", summary.admins],
                ["Customers", summary.customers],
                ["Super admins", summary.superadmins],
                ["Active subscriptions", summary.subscriptionsActive],
                ["Trialing", summary.subscriptionsTrialing],
                ["Stripe Connect linked", summary.stripeConnectAccounts],
              ].map(([label, val]) => (
                <Grid item xs={12} sm={6} md={4} key={label}>
                  <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="h5" fontWeight={800}>
                      {val}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3, maxWidth: 720 }}>
            Use <strong>Users & billing</strong> to change roles, subscription labels, Stripe ids (read-only copy), feature flags, and internal notes. For live Stripe changes, operators still use Dashboard → Billing.
          </Typography>
        </TabPanel>

        <TabPanel hidden={tab !== 1}>
          <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  size="small"
                  label="Search email or name"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  fullWidth
                  onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  size="small"
                  label="Role"
                  value={roleFilter}
                  onChange={(e) => {
                    setRoleFilter(e.target.value);
                    setPage(0);
                  }}
                  fullWidth
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="customer">customer</MenuItem>
                  <MenuItem value="admin">admin</MenuItem>
                  <MenuItem value="superadmin">superadmin</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={5}>
                <Button variant="outlined" onClick={() => applyFilters()}>
                  Apply search
                </Button>
              </Grid>
            </Grid>
          </Paper>
          <Box sx={{ height: 560, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={usersLoading}
              pagination
              paginationMode="server"
              page={page}
              pageSize={pageSize}
              rowCount={total}
              onPageChange={(p) => setPage(p)}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              density="compact"
            />
          </Box>
        </TabPanel>

        <TabPanel hidden={tab !== 2}>
          <SuperAdminCrmPanel />
        </TabPanel>
      </Box>

      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit user</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {edit.email}
          </Typography>
          <TextField
            margin="dense"
            label="First name"
            value={edit.firstname}
            onChange={(e) => setEdit((s) => ({ ...s, firstname: e.target.value }))}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Last name"
            value={edit.lastname}
            onChange={(e) => setEdit((s) => ({ ...s, lastname: e.target.value }))}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Phone"
            value={edit.phone}
            onChange={(e) => setEdit((s) => ({ ...s, phone: e.target.value }))}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Address"
            value={edit.address}
            onChange={(e) => setEdit((s) => ({ ...s, address: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            margin="dense"
            select
            label="Role"
            value={edit.role}
            onChange={(e) => setEdit((s) => ({ ...s, role: e.target.value }))}
            fullWidth
          >
            <MenuItem value="customer">customer</MenuItem>
            <MenuItem value="admin">admin</MenuItem>
            <MenuItem value="superadmin">superadmin</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="Quote notify email (operators)"
            value={edit.quoteNotificationEmail}
            onChange={(e) => setEdit((s) => ({ ...s, quoteNotificationEmail: e.target.value }))}
            fullWidth
          />
          <TextField
            margin="dense"
            select
            label="Plan key"
            value={edit.planKey || ""}
            onChange={(e) => setEdit((s) => ({ ...s, planKey: e.target.value }))}
            fullWidth
          >
            <MenuItem value="">(unspecified)</MenuItem>
            <MenuItem value="solo">solo</MenuItem>
            <MenuItem value="growing">growing</MenuItem>
            <MenuItem value="enterprise">enterprise</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="Subscription status (support override)"
            value={edit.subscriptionStatus}
            onChange={(e) => setEdit((s) => ({ ...s, subscriptionStatus: e.target.value }))}
            fullWidth
            helperText="Normally updated by Stripe webhooks; edit for manual reconciliation only."
          />
          <TextField margin="dense" label="Stripe customer id" value={edit.billingID} fullWidth InputProps={{ readOnly: true }} />
          <TextField margin="dense" label="Subscription id" value={edit.subscriptionId} fullWidth InputProps={{ readOnly: true }} />
          <TextField margin="dense" label="Stripe Connect account" value={edit.stripeID} fullWidth InputProps={{ readOnly: true }} />
          <TextField
            margin="dense"
            label="Internal platform note"
            value={edit.platformNote}
            onChange={(e) => setEdit((s) => ({ ...s, platformNote: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Feature flags (tenant)
          </Typography>
          <FormControlLabel
            control={<Switch checked={edit.featureEmbedScript} onChange={(e) => setEdit((s) => ({ ...s, featureEmbedScript: e.target.checked }))} />}
            label="Embed / script features"
          />
          <FormControlLabel
            control={
              <Switch checked={edit.featureMultiParcelQuotes} onChange={(e) => setEdit((s) => ({ ...s, featureMultiParcelQuotes: e.target.checked }))} />
            }
            label="Multi-parcel quotes"
          />
          <FormControlLabel
            control={<Switch checked={edit.featureStripeConnect} onChange={(e) => setEdit((s) => ({ ...s, featureStripeConnect: e.target.checked }))} />}
            label="Stripe Connect (customer billing)"
          />
          <FormControlLabel
            control={<Switch checked={edit.featureAiAssistBeta} onChange={(e) => setEdit((s) => ({ ...s, featureAiAssistBeta: e.target.checked }))} />}
            label="AI assist (beta)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
